import { Checkbox, Typography } from "antd";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import "../../../assets/css/setting.css";
import HRadioGroup from "../../../components/custom/input/radio/HRadioGroup";
import HTextArea from "../../../components/custom/input/textarea/HTextArea";
import { AppContext } from "../../../context/AppContext";
import EmailComponent from "../../Setting/EmailComponent";
import type { WorkflowEnumItem } from "../hooks/useWorkflowEnums";
import type { ActionNodeData } from "../nodes/ActionNode";
import { useWorkflowStore } from "../store";
import { Trigger } from "../nodes/TriggerNode";
import { selectSelectedStep, selectSelectedStepIndex } from "../store/selectors";
import { getActionIconAndClass, getFormTypeForAction } from "../utils/actionCodeMapping";
import { ACTION_HELP_CONTENT } from "../utils/helpContentConfig";
import TimeSelector from "./TimeSelector";
import { AutomatedResponseForm } from "./AutomatedResponseForm";
import { ConfigPanelFooter } from "./ConfigPanelFooter";
import { ConfigPanelShell } from "./ConfigPanelShell";
import { StepTypePicker } from "./StepTypePicker";
import { useStepConfigPanel } from "../hooks/useStepConfigPanel";
import { getActionDraftResetForTypePicker } from "./actionDraftPickerReset";
import { getNotifyUserStaticMessageFromActionTypes } from "../utils/notifyUserStaticMessage";

const { Text } = Typography;

export interface ActionConfigPanelProps {
    actionTypes: WorkflowEnumItem[];
    isNarrowLayout: boolean;
}

const NotifyUserForm: React.FC<{
    data: ActionNodeData;
    update: (d: Partial<ActionNodeData>) => void;
    staticMessage: string;
}> = ({ data, update, staticMessage }) => {
    const [appProperties] = useContext(AppContext);
    const [emailValidationErrors, setEmailValidationErrors] = useState<Record<string, string> | null>(null);

    const emailFieldConfig = useMemo(
        () => ({
            key: "additionalEmails",
            placeholder: "Enter an email address",
            onchange: (_parentKey: string, _key: string, value: string[]) => {
                update({ additionalEmails: value });
            },
        }),
        [update]
    );

    const handleRecordOwnerChange = (e: { target: { checked: boolean } }) => {
        update({ recordOwner: e.target.checked });
        setEmailValidationErrors(null);
    };
    const handleAccountOwnerChange = (e: { target: { checked: boolean } }) => {
        update({ accountOwner: e.target.checked });
        setEmailValidationErrors(null);
    };
    const handleAdditionalEmailsChange = (e: { target: { checked: boolean } }) => {
        update({
            includeAdditionalEmails: e.target.checked,
            additionalEmails: e.target.checked ? (data.additionalEmails ?? []) : [],
        });
        setEmailValidationErrors(null);
    };

    const isAdditionalEmailsEnabled = data.includeAdditionalEmails ?? false;

    return (
        <div className="d-flex flex-column gap-2">
            <div className="d-flex flex-column gap-1">
                <Text className="hs-fs-12 hs-color-BlackRussian hs-fw-500">Send Email to</Text>
                <div className="d-flex flex-column gap-3 mt-2">
                    <Checkbox
                        className="hs-fs-12"
                        checked={data.recordOwner ?? false}
                        onChange={handleRecordOwnerChange}
                    >
                        Record Owner
                    </Checkbox>
                    <Checkbox
                        className="hs-fs-12"
                        checked={data.accountOwner ?? false}
                        onChange={handleAccountOwnerChange}
                    >
                        Account Owner ({appProperties?.extensionInstalledUserEmailId || ""})
                    </Checkbox>
                    <Checkbox
                        className="hs-fs-12"
                        checked={isAdditionalEmailsEnabled}
                        onChange={handleAdditionalEmailsChange}
                    >
                        Additional Email Addresses
                    </Checkbox>
                </div>
            </div>
            {isAdditionalEmailsEnabled && (
                <div className="py-3 m-1 mt-3 mt-sm-2">
                    <EmailComponent
                        parentKey="workflowNotify"
                        emailList={data.additionalEmails ?? []}
                        field={emailFieldConfig}
                        setErrorObj={setEmailValidationErrors}
                    />
                    {emailValidationErrors?.["Email notification"] && (
                        <div className="hs-fs-12 hs-color-red mt-1">{emailValidationErrors["Email notification"]}</div>
                    )}
                </div>
            )}
            <div className="d-flex flex-column gap-1">
                <Text className="hs-fw-500 hs-fs-12 hs-color-BlackRussian p-1">Message</Text>
                <HTextArea
                    value={staticMessage}
                    readOnly
                    disabled
                    className="w-100"
                    rows={4}
                    autoSize={{ minRows: 4 }}
                />
            </div>
        </div>
    );
};

const ADD_NOTE_FORM_FIELDS = [
    {
        key: "logRecordsFor",
        label: "Where would you prefer to log received message?",
        options: [
            { label: "All matching records", value: "all" },
            { label: "Recent record", value: "recent" },
        ],
    },
];

const AddNoteForm: React.FC<{ data: ActionNodeData; update: (d: Partial<ActionNodeData>) => void }> = ({
    data,
    update,
}) => (
    <div>
        {ADD_NOTE_FORM_FIELDS.map((field) => (
            <div key={field.key} className="mb-3">
                <HRadioGroup
                    groupLabel={field.label}
                    options={field.options}
                    radioGroupProps={{
                        value: data[field.key as keyof ActionNodeData] ?? field.options[0].value,
                        onChange: (e: { target: { value: string } }) =>
                            update({ [field.key]: e.target.value } as Partial<ActionNodeData>),
                    }}
                />
            </div>
        ))}
    </div>
);

const LOG_ACTIVITY_FORM_FIELDS = [
    {
        key: "logRecordAsThread",
        label: "Merge all activities into a single activity as notes?",
        options: [
            { label: "Yes", value: "true" },
            { label: "No", value: "false" },
        ],
    },
    {
        key: "logRecordsFor",
        label: "Where would you prefer to log received message?",
        options: [
            { label: "All matching records", value: "all" },
            { label: "Recent record", value: "recent" },
        ],
    },
];

const LogActivityForm: React.FC<{ data: ActionNodeData; update: (d: Partial<ActionNodeData>) => void }> = ({
    data,
    update,
}) => (
    <div>
        {LOG_ACTIVITY_FORM_FIELDS.map((field) => (
            <div key={field.key} className="mb-3">
                <HRadioGroup
                    groupLabel={field.label}
                    options={field.options}
                    radioGroupProps={{
                        value: data[field.key as keyof ActionNodeData] ?? field.options[0].value,
                        onChange: (e: { target: { value: string } }) =>
                            update({ [field.key]: e.target.value } as Partial<ActionNodeData>),
                    }}
                />
            </div>
        ))}
    </div>
);

const DELAY_TIME_FIELDS = [
    { key: "delayDays", min: 0, max: 30, label: "Days" },
    { key: "delayHours", min: 0, max: 23, label: "Hours" },
    { key: "delayMinutes", min: 15, max: 59, label: "Minutes" },
];

const DelayForm: React.FC<{ data: ActionNodeData; update: (d: Partial<ActionNodeData>) => void }> = ({
    data,
    update,
}) => (
    <div>
        <Text className="hs-fw-600 hs-fs-12 hs-color-BlackRussian mb-2">Duration</Text>
        <TimeSelector
            value={{
                delayDays: data.delayDays ?? 0,
                delayHours: data.delayHours ?? 0,
                delayMinutes: data.delayMinutes ?? 15,
            }}
            onChange={(val: { delayDays: number; delayHours: number; delayMinutes: number }) =>
                update({
                    delayDays: val.delayDays ?? 0,
                    delayHours: val.delayHours ?? 0,
                    delayMinutes: val.delayMinutes ?? 15,
                })
            }
            fields={DELAY_TIME_FIELDS}
        />
        <Text className="hs-fs-11 hs-color-mediumDark d-block mb-2">Delay must be between 15 minutes and 30 days.</Text>
    </div>
);

const CreateLeadForm: React.FC<{ data: ActionNodeData; update: (d: Partial<ActionNodeData>) => void }> = ({
    data,
    update,
}) => (
    <div className="px-1 d-flex flex-column gap-3">
        <HRadioGroup
            groupLabel="Automatically create a new lead?"
            options={[
                { label: "Yes", value: "true" },
                { label: "No", value: "false" },
            ]}
            radioGroupProps={{
                value: data.createLead ?? "true",
                onChange: (e: { target: { value: string } }) =>
                    update({ createLead: (e.target as HTMLInputElement).value }),
            }}
        />
    </div>
);

function renderActionFormContent(
    actionTypeOrCode: string,
    data: ActionNodeData,
    update: (d: Partial<ActionNodeData>) => void,
    nodeId: string,
    notifyUserStaticMessage: string
): React.ReactNode {
    const formType = getFormTypeForAction(actionTypeOrCode);
    switch (formType) {
        case "AutomatedResponse":
            return <AutomatedResponseForm key={nodeId || "action-draft"} data={data} update={update} />;
        case "NotifyUser":
            return <NotifyUserForm data={data} update={update} staticMessage={notifyUserStaticMessage} />;
        case "CreateLead":
            return <CreateLeadForm data={data} update={update} />;
        case "Delay":
            return <DelayForm data={data} update={update} />;
        case "AddNote":
            return <AddNoteForm data={data} update={update} />;
        case "LogActivity":
            return <LogActivityForm data={data} update={update} />;
        default:
            return (
                <div className="px-1 py-3">
                    <Text className="hs-fs-12 hs-color-mediumDark">
                        Configuration for this action type is not yet available.
                    </Text>
                </div>
            );
    }
}

export const ActionConfigPanel: React.FC<ActionConfigPanelProps> = ({ actionTypes, isNarrowLayout }) => {
    const selectedStep = useWorkflowStore(selectSelectedStep);
    const selectedStepIndex = useWorkflowStore(selectSelectedStepIndex);
    const updateStepData = useWorkflowStore((s) => s.updateStepData);
    const deleteStep = useWorkflowStore((s) => s.deleteStep);
    const selectNode = useWorkflowStore((s) => s.selectNode);
    const incrementCloseSignal = useWorkflowStore((s) => s.incrementCloseSignal);
    const triggerType = useWorkflowStore((s) => s.triggerType);

    const nodeId = selectedStep?.id ?? "";
    const persistedData = useMemo((): ActionNodeData => {
        const raw = (selectedStep?.data as ActionNodeData) ?? {};
        const ft = getFormTypeForAction(raw.actionCode ?? raw.actionType ?? "");
        if (ft === "NotifyUser" && raw.message !== undefined) {
            const rest = { ...raw };
            delete rest.message;
            return rest;
        }
        return raw;
    }, [selectedStep?.data]);

    const notifyUserStaticMessage = useMemo(
        () => getNotifyUserStaticMessageFromActionTypes(actionTypes),
        [actionTypes]
    );

    const transformActionDraft = useCallback((prev: ActionNodeData, partial: Partial<ActionNodeData>) => {
        const next = { ...prev, ...partial };
        const ft = getFormTypeForAction(next.actionCode ?? next.actionType ?? "");
        if (ft === "NotifyUser") {
            const rest = { ...next };
            delete rest.message;
            return rest;
        }
        return next;
    }, []);

    const { draftData, updateDraft, commitDraft, hasDraftChanges, setInitialData } = useStepConfigPanel<ActionNodeData>(
        nodeId,
        persistedData,
        updateStepData,
        { transformDraft: transformActionDraft }
    );

    const prevNodeIdRef = useRef(nodeId);

    useEffect(() => {
        if (prevNodeIdRef.current !== nodeId) {
            prevNodeIdRef.current = nodeId;
        }
    }, [nodeId]);

    const isPlaceholderStep =
        (persistedData.isPlaceholder ?? false) || (!persistedData.actionType && !persistedData.actionCode);
    const canCommit = Boolean(draftData.actionCode || draftData.actionType);
    const canAddStep = canCommit;
    const canSaveChanges = hasDraftChanges;

    const handleCloseDeselect = () => {
        if (isPlaceholderStep && selectedStep?.id) {
            deleteStep(selectedStep.id);
        }
        selectNode(null);
    };

    const handleBackToPicker = () => {
        updateDraft(getActionDraftResetForTypePicker());
    };
    const showPicker =
        !persistedData.actionType && !persistedData.actionCode && !draftData.actionType && !draftData.actionCode;

    const handleSelectActionType = (code: string, title: string) => {
        const formType = getFormTypeForAction(code);
        const initialPartial: Partial<ActionNodeData> = {
            actionCode: code,
            actionType: formType,
            title,
            isPlaceholder: false,
        };
        if (formType === "AddNote" || formType === "LogActivity") {
            initialPartial.logRecordAsThread = "false";
            initialPartial.logRecordsFor = "all";
        }
        if (formType === "Delay") {
            initialPartial.delayDays = 0;
            initialPartial.delayHours = 0;
            initialPartial.delayMinutes = 15;
        }
        if (formType === "CreateLead") {
            initialPartial.createLead = "true";
        }
        updateDraft(initialPartial);
    };

    const handlePrimaryClick = () => {
        if (isPlaceholderStep) {
            if (!canAddStep || selectedStepIndex < 0 || !selectedStep || !nodeId) return;
            const dataToCommit = { ...draftData, isPlaceholder: false };
            updateStepData(nodeId, dataToCommit);
            setInitialData(dataToCommit);
            if (isNarrowLayout) {
                incrementCloseSignal();
                selectNode(null);
            }
            return;
        }
        if (!hasDraftChanges) return;
        commitDraft();
        if (isNarrowLayout) {
            incrementCloseSignal();
            selectNode(null);
        }
    };

    if (showPicker) {
        return (
            <ConfigPanelShell
                title="Actions"
                onClose={handleCloseDeselect}
                footer={<ConfigPanelFooter backOnly isPlaceholder={isPlaceholderStep} />}
            >
                <StepTypePicker
                    items={actionTypes}
                    onSelect={handleSelectActionType}
                    getIcon={(code) => getActionIconAndClass(code).icon}
                />
            </ConfigPanelShell>
        );
    }

    const displayTitle = draftData.title ?? draftData.actionType ?? draftData.actionCode;
    const formType = getFormTypeForAction(draftData.actionCode ?? draftData.actionType ?? "");
    const helpContent = ACTION_HELP_CONTENT[formType] ?? ACTION_HELP_CONTENT.default;

    return (
        <ConfigPanelShell
            title={displayTitle ?? "Action"}
            onClose={isPlaceholderStep ? handleBackToPicker : handleCloseDeselect}
            helpContent={helpContent}
            footer={
                <ConfigPanelFooter
                    primaryLabel={isPlaceholderStep ? "Apply" : "Save Changes"}
                    primaryDisabled={isPlaceholderStep ? !canAddStep : !canSaveChanges}
                    onPrimaryClick={handlePrimaryClick}
                    isPlaceholder={isPlaceholderStep}
                    hideDelete={triggerType === Trigger.Message.Outbound}
                    onBackToPicker={isPlaceholderStep ? handleBackToPicker : undefined}
                />
            }
        >
            {renderActionFormContent(
                draftData.actionCode ?? draftData.actionType ?? "",
                draftData,
                updateDraft,
                nodeId,
                notifyUserStaticMessage
            )}
        </ConfigPanelShell>
    );
};
